import { signatureColors, validationOptions, fontFamilies } from './constants';

type FontFamily = typeof fontFamilies[number];


export enum FieldTypes {
  Checkbox = 'checkbox',
  Date = 'date',
  Initials = 'initials',
  Signature = 'signature',
  Text = 'text',
  Dropdown = 'dropdown',
  Radio = 'radiobutton',
  Hyperlink = 'hyperlink',
}

export enum AutofillFieldTypes {
  FullName = 'name',
  LastName = 'lastName',
  FirstName = 'firstName',
  Company = 'company',
  Email = 'email',
  Title = 'title',
}

export interface BaseField {
  id: string,
  // API ID must be validated in realtime. It can't wait until the user presses
  // "Continue" to be fixed because we rely on using the ID as the key for
  // updating fields. If we did update the ID and then wait until the end to
  // validate, it would be impossible to fix because `updateFields({ [oldID]:
  // {id: newID}})` will update BOTH fields.
  invalidApiId?: string
  pageIndex: number,
  name?: string,
  x: number,
  y: number,
  required?: boolean,
  // This is used for Conditional Logic. When a required field is hidden,
  // required is stored here as a backup in case the field is shown again.
  originalRequired?: boolean,
  signer: string,
  width: number,
  height: number,
  documentId: string,
  hidden?: boolean,
  readOnly?: boolean,
  // AutoFill
  autoFillType?: AutofillFieldTypes | null,
  linkId?: string | null,
}

export type BaseFieldTypes = {
  type: FieldTypes,
};

export interface CheckboxField extends BaseField {
  type: FieldTypes.Checkbox,
  checked?: boolean | null,
  group?: string | null,
  requirement?: string | null,
  groupLabel?: string | null,
  isEditableMergeField?: boolean,
}

export interface RadioField extends BaseField {
  type: FieldTypes.Radio,
  checked: boolean,
  // Radio buttons MUST be part of a group
  group: string,
  requirement: string | null,
  groupLabel?: string | null,
}

export interface TextField extends BaseField {
  type: FieldTypes.Text,
  placeholder?: string,
  lineHeight?: number,
  fontFamily?: FontFamily,
  fontSize?: number,
  value?: string | null;
  validationType?: keyof typeof validationOptions,
  masked?: boolean,
  isEditableMergeField?: boolean,
  originalFontSize?: number,
  // `lines` is just `value` with newlines added for explicit word wrapping. We
  // need it because our current Cairo based overlay system doesn't contain a
  // text layout engine, so lines have to be pre-computed.
  lines?: string,
}

export interface DropdownField extends BaseField {
  type: FieldTypes.Dropdown,
  options: Array<string>,
  value?: string | null,
  fontFamily?: FontFamily,
  fontSize?: number,
}

export interface DateField extends BaseField {
  type: FieldTypes.Date,
  placeholder?: string,
  dateFormat?: string,
  fontFamily?: FontFamily,
  fontSize?: number,
  value?: string | null,
}

export interface SignatureField extends BaseField {
  type: FieldTypes.Signature,
  color?: typeof signatureColors[number],
  signature?: string,
}

export interface InitialsField extends BaseField {
  type: FieldTypes.Initials,
  color?: typeof signatureColors[number],
  signature?: string,
}

export interface HyperlinkField extends BaseField {
  type: FieldTypes.Hyperlink,
  lineHeight?: number,
  fontFamily?: FontFamily,
  fontSize?: number,
  value: string | null,
  url: string,
}

export type Field =
  | TextField
  | CheckboxField
  | DateField
  | SignatureField
  | InitialsField
  | DropdownField
  | RadioField
  | HyperlinkField

export type DocumentRotation = 0 | 1 | 2 | 3
export type Document = {
  id: string,
  name: string,
  snapshotGuid: string,
  editable: boolean,
};

export type Page = {

  src: string,
  width: number,
  height: number,
  documentId: string,
  orientation: 0 | 1,
  cdnSrc?: string,
};

export type SigFieldTypes = SignatureField | InitialsField;

export type Signer = {
  id: number | string,
  name: string,
  email: string | null,
  order?: number,
  deleted?: boolean,
};

export type SignerOrder = {
  [signerId in Signer['id']]: number;
};

export type ValidationError = {
  id: string | null,
  property?: string,
  textId: string,
  values?: {
    name: string,
    signerOrder: number,
  },
}

export type MergeField = {
  name: string,
  type: FieldTypes.Text | FieldTypes.Checkbox
}

export interface FieldOverrideProps {
  autoFillType?: string | null,
  signer?: string | null,
  isDisabled?: boolean,
}

export type Preparer = {
  email: string,
  firstName: string,
  lastName: string
  title: string,
  company: string,
};
